import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DigipingSmartRail } from "../digiping";
import { fetchSmartTrail } from "./funtionsBD/digipingSmartService";
import {
  acknowledgeFlttRankingNotice,
  getPlayerProfileStats,
} from "./funtionsBD/Mainservice";
import { FlttRankingSlip } from "./FlttRankingSlip";
import { canShowFlttRankingChart } from "../utils/flttRankingDisplay";
import { CoachObjectiveMetricPicker } from "./CoachObjectiveMetricPicker";
import { SprintExerciseTable } from "./SprintExerciseTable";
import { openDigitalTwin } from "./HomeDailyFocus";
import { openPlayerTechnicalProfiling } from "../tour/technicalProfilingPrompt";
import { readStoredUser } from "../utils/homeRole";
import { useTourDemoActive } from "../tour/useTourDemoActive";
import { demoSmartTrail } from "../tour/tourDemoFixtures";
import {
  isKnownSignupSeasonResultLabel,
  isSignupSeasonResultNoise,
  resolveSignupSeasonResult,
} from "../utils/playerSeasonResult";
import { ExerciseTypeMark } from "./ExerciseTypeMark";
import { competencyBlockIconSrc } from "../utils/competencyBlockIcon";
import "./ProfileSmartTab.css";
import "./HomePlayerPath.css";

const PROFILING_CB_ORDER = {
  fundamentals: 1,
  first_contact: 2,
  forehand: 3,
  backhand: 4,
  lateral_connection: 5,
  effects: 6,
  playstyles: 7,
  game_scheme: 8,
};

const CB_KEY_LABELS = {
  fundamentals: { en: "Fundamentals", fr: "Fondamentaux", es: "Fundamentos", de: "Grundlagen" },
  first_contact: { en: "First contact", fr: "Premier contact", es: "Primer contacto", de: "Erster Kontakt" },
  forehand: { en: "Forehand", fr: "Coup droit", es: "Derecha", de: "Vorhand" },
  backhand: { en: "Backhand", fr: "Revers", es: "Revés", de: "Rückhand" },
  lateral_connection: { en: "Footwork", fr: "Déplacement", es: "Desplazamiento", de: "Beinarbeit" },
  effects: { en: "Spin & effects", fr: "Effets", es: "Efectos", de: "Effekte" },
  playstyles: { en: "Playing style", fr: "Style de jeu", es: "Estilo de juego", de: "Spielstil" },
  game_scheme: { en: "Game scheme", fr: "Schéma de jeu", es: "Esquema de juego", de: "Spielschema" },
};

const DIM_HINT_KEYS = {
  S: "smartDimSHint",
  M: "smartDimMHint",
  A: "smartDimAHint",
  R: "smartDimRHint",
  T: "smartDimTHint",
};

function formatDate(value, lang) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  try {
    return d.toLocaleDateString(lang || "en", { day: "numeric", month: "short" });
  } catch {
    return String(value).slice(0, 10);
  }
}

function cbLabel(cbKey, lang) {
  const key = String(cbKey || "").toLowerCase();
  const labels = CB_KEY_LABELS[key];
  if (!labels) return key.replace(/_/g, " ");
  const shortLang = (lang || "en").split("-")[0];
  return labels[shortLang] || labels.en || key;
}

function exerciseStatusKey(status) {
  const st = String(status || "assigned").toLowerCase();
  return `smartExStatus_${st}`;
}

function exerciseStatusChipClass(status) {
  const st = String(status || "").toLowerCase();
  if (st === "assigned") return "pst-chip--ok";
  if (st === "pending_coach") return "pst-chip--now";
  if (st === "proposed") return "pst-chip--ai";
  if (st === "rejected") return "pst-chip--warn";
  return "pst-chip--now";
}

function questProgress(exercise) {
  const status = String(exercise?.status || "").toLowerCase();
  const times = Number(exercise?.times_performed) || 0;
  if (!exercise?.title) return 0;
  if (status === "rejected") return 8;
  if (status === "proposed") return 18;
  if (status === "pending_coach") return 32;
  if (status === "assigned") return Math.min(100, 45 + times * 18);
  return 22;
}

function coachDimStatus(coachAgreement, coachPendingDims, dim) {
  const covers = Array.isArray(coachAgreement?.covers) ? coachAgreement.covers : ["A", "R"];
  const coversDim = covers.includes(dim);
  const pendingLetter = (coachPendingDims || []).includes(dim);
  if (coversDim && coachAgreement?.status === "approved") return "approved";
  if (coversDim && coachAgreement?.status === "rejected") return "rejected";
  if (pendingLetter || (coversDim && coachAgreement?.status === "pending")) return "pending";
  return "none";
}

function pickMilestone(goal, milestones, used) {
  const list = milestones || [];
  const claim = (idx) => {
    if (idx < 0 || used.has(idx)) return null;
    used.add(idx);
    return list[idx];
  };
  const byAttr = list.findIndex(
    (m, i) =>
      !used.has(i) &&
      m.attributeId != null &&
      goal.attribute_id != null &&
      String(m.attributeId) === String(goal.attribute_id)
  );
  if (byAttr >= 0) return claim(byAttr);
  const needle = String(goal.label || "").trim().toLowerCase();
  if (needle) {
    const byLabel = list.findIndex(
      (m, i) => !used.has(i) && String(m.label || "").toLowerCase().includes(needle)
    );
    if (byLabel >= 0) return claim(byLabel);
  }
  if (goal.is_primary) {
    const mid = list.findIndex((m, i) => !used.has(i) && m.horizon === "mid");
    if (mid >= 0) return claim(mid);
  }
  return claim(list.findIndex((_, i) => !used.has(i)));
}

function buildQuests(data) {
  const exercises = data?.goalExercises || [];
  let goals = [...(data?.goals || [])];
  if (!goals.length) {
    for (const e of exercises) {
      if (!e.goalLabel && !e.title) continue;
      goals.push({
        attribute_id: e.attributeId,
        label: e.goalLabel || e.title,
        is_primary: e.goalRole !== "secondary",
      });
    }
  }
  if (!goals.length && data?.personalizedExercise?.title) {
    goals.push({
      attribute_id: data.personalizedExercise.attributeId,
      label: data.personalizedExercise.goalLabel || data.personalizedExercise.title,
      is_primary: true,
    });
  }
  if (!goals.length) {
    const fallbackLabel =
      data?.pathMeta?.leisureQuest?.questTitle ||
      data?.trail?.slots?.S?.labels?.primaryFocus ||
      data?.slots?.S?.labels?.primaryFocus ||
      null;
    if (fallbackLabel) {
      goals.push({
        attribute_id: data?.trail?.slots?.S?.primaryFocusId || data?.slots?.S?.primaryFocusId || null,
        label: fallbackLabel,
        is_primary: true,
      });
    }
  }
  const used = new Set();
  const takenEx = new Set();
  const leisureQuest =
    data?.pathMeta?.leisureQuest?.questTitle || data?.slots?.leisure?.questTitle || null;
  const seasonPrimary = data?.seasonHorizon?.primaryGoalLabel || null;
  const slotPrimary = data?.trail?.slots?.S?.labels?.primaryFocus || data?.slots?.S?.labels?.primaryFocus || null;
  return goals.map((goal) => {
    // Keep training-axis labels only — never substitute season results here.
    const displayLabel =
      (goal.is_primary && (leisureQuest || seasonPrimary || slotPrimary || goal.label)) ||
      goal.label;
    const displayGoal = { ...goal, label: displayLabel };
    const byAttr = exercises.find(
      (e, i) =>
        !takenEx.has(i) &&
        e.attributeId != null &&
        goal.attribute_id != null &&
        String(e.attributeId) === String(goal.attribute_id)
    );
    const byRole = exercises.find((e, i) => {
      if (takenEx.has(i) || !e.title) return false;
      if (goal.is_primary) return e.goalRole !== "secondary";
      return e.goalRole === "secondary";
    });
    const exercise =
      byAttr ||
      byRole ||
      (goal.is_primary && data?.personalizedExercise?.title ? data.personalizedExercise : null);
    if (exercise) {
      const idx = exercises.indexOf(exercise);
      if (idx >= 0) takenEx.add(idx);
    }
    return {
      goal: displayGoal,
      exercise,
      milestone: pickMilestone(displayGoal, data?.milestones, used),
    };
  });
}

function SeasonBanner({ seasonHorizon, seasonQuest = null, trail = null, t }) {
  const slotsA = trail?.slots?.A || {};
  const label = resolveSignupSeasonResult(
    {
      ...(seasonQuest && typeof seasonQuest === "object" ? seasonQuest : {}),
      finalObjective:
        seasonQuest?.finalObjective || slotsA.finalObjective || null,
      expectedResult:
        seasonQuest?.expectedResult || slotsA.seasonQuest?.expectedResult || null,
      expectedResultDraft:
        seasonQuest?.expectedResultDraft ||
        slotsA.seasonQuest?.expectedResultDraft ||
        null,
    },
    seasonHorizon,
    slotsA,
    t
  );
  if (!label) return null;
  return (
    <div className="pst-season" data-tour-id="profile-smart-expected-results">
      <div className="pst-season__copy">
        <span className="pst-season__kicker">
          {t("smartExpectedResultsTitle", { defaultValue: "Expected Results" })}
        </span>
        <strong className="pst-season__title">{label}</strong>
      </div>
    </div>
  );
}

function ProgressBar({ value, accent }) {
  const p = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="pst-bar" role="progressbar" aria-valuenow={p} aria-valuemin={0} aria-valuemax={100}>
      <span style={{ width: `${p}%`, background: accent }} />
    </div>
  );
}

function GoalQuestCard({
  quest,
  primary,
  coachA,
  userId,
  t,
  seasonMonths,
  isLeisure = false,
}) {
  const { goal, exercise } = quest;
  const [editing, setEditing] = useState(false);
  const progress = questProgress(exercise);
  const metrics =
    exercise?.selected_metrics ||
    (exercise?.selected_metric_kinds || []).map((kind) => ({ kind, label: kind }));
  const coachKey =
    coachA === "approved"
      ? "smartTargetApproved"
      : coachA === "rejected"
        ? "smartTargetRejected"
        : coachA === "pending"
          ? "smartTargetPendingCoach"
          : "smartTargetNone";
  const goalTitle = goal.label || t("smartSeasonHorizonFallback");

  return (
    <article className={`pst-quest ${primary ? "is-primary" : "is-secondary"}`}>
      <header className="pst-quest__head">
        <div>
          <span className={`pst-chip ${primary ? "pst-chip--pri" : "pst-chip--sec"}`}>
            {primary ? t("smartAxisPrimary") : t("smartAxisSecondary")}
          </span>
          <span className="pst-chip pst-chip--horizon">
            {t("smartMidTermAxis")} · {t("smartSeasonMonthsLeft", { count: seasonMonths })}
          </span>
        </div>
        <span className="pst-quest__pct">{progress}%</span>
      </header>
      <h3 className="pst-quest__title">{goalTitle}</h3>
      <p className="pst-quest__axis">
        {t("smartTrainingObjectiveFocus", {
          defaultValue: "Training objective — linked exercises and metrics",
        })}
      </p>
      <ProgressBar value={progress} accent={primary ? "linear-gradient(90deg, #22d3ee, #0891b2)" : "linear-gradient(90deg, #fbbf24, #d97706)"} />

      <section className="pst-quest__drill">
        <span className="pst-quest__kicker">
          {isLeisure ? t("smartLeisureDrill") : t("smartSubObjective")}
        </span>
        {exercise?.title ? (
          <>
            <h4 className="pst-quest__drill-title">{exercise.title}</h4>
            {exercise.objective ? <p className="pst-muted">{exercise.objective}</p> : null}
            {!exercise.objective && exercise.summary ? <p className="pst-muted">{exercise.summary}</p> : null}
            <div className="pst-quest__drill-meta">
              {exercise.cb_name ? (
                <span className="pst-chip pst-chip--ai" title={t("smartCompetencyBlock")}>
                  {exercise.cb_name}
                </span>
              ) : null}
              <span className={`pst-chip ${exerciseStatusChipClass(exercise.status)}`}>
                {t(exerciseStatusKey(exercise.status), { defaultValue: exercise.status })}
              </span>
              {exercise.pendingChange?.title ? (
                <span className="pst-chip pst-chip--now">
                  {t("smartExercisePendingChange", { title: exercise.pendingChange.title })}
                </span>
              ) : null}
              <span className="pst-chip">
                {exercise.times_performed > 0
                  ? t("smartPerformedTimes", { count: exercise.times_performed })
                  : t("smartNotPerformedYet")}
              </span>
              <span className={`pst-chip ${coachA === "approved" ? "pst-chip--ok" : coachA === "rejected" ? "pst-chip--warn" : "pst-chip--now"}`}>
                {t(coachKey)}
              </span>
            </div>
            {metrics.length ? (
              <p className="pst-muted pst-metrics">
                M · {metrics.map((m) => m.label || m.kind).join(", ")}
              </p>
            ) : null}
            {userId && exercise.exerciseId ? (
              <button type="button" className="pst-link-btn" onClick={() => setEditing((v) => !v)}>
                {editing ? t("close") : t("smartEditMetrics")}
              </button>
            ) : null}
            {editing && userId && exercise.exerciseId ? (
              <CoachObjectiveMetricPicker
                playerUserId={userId}
                exId={exercise.exerciseId}
                goalRole={exercise.goalRole || (primary ? "primary" : "secondary")}
                goalLabel={exercise.goalLabel || goal.label}
                mode="edit"
                compact
                onSaved={() => setEditing(false)}
              />
            ) : null}
          </>
        ) : (
          <p className="pst-muted">{t("smartNoExerciseYet")}</p>
        )}
      </section>
    </article>
  );
}

function blockAxisRoles(block) {
  const raw = block?.axes?.length
    ? block.axes
    : block?.goal_role
      ? [{ goal_role: block.goal_role }]
      : [];
  const roles = [];
  for (const axis of raw) {
    const role = axis?.goal_role;
    if (role !== "primary" && role !== "secondary") continue;
    if (roles.includes(role)) continue;
    roles.push(role);
  }
  return roles;
}

function ExerciseRow({ ex, t }) {
  return (
    <div className="pst-ex">
      <div className="pst-ex__main">
        <span className="pst-ex__name">{ex.name}</span>
        <div className="pst-ex__meta">
          {ex.in_current_program ? <span className="pst-chip pst-chip--now">{t("smartCurrentProgram")}</span> : null}
          {ex.created_with_digiping ? <span className="pst-chip pst-chip--ai">{t("smartCoCreated")}</span> : null}
          {ex.native_cb_name ? (
            <span className="pst-chip" title={t("smartCompetencyBlock")}>
              {ex.native_cb_name}
            </span>
          ) : null}
          {ex.goal_role === "primary" ? <span className="pst-chip pst-chip--pri">{t("smartAxisPrimary")}</span> : null}
          {ex.goal_role === "secondary" ? <span className="pst-chip pst-chip--sec">{t("smartAxisSecondary")}</span> : null}
          {ex.gamification?.niveaux?.length ? (
            <span className="pst-chip" title={t("playerPathChallenge")}>
              N1–N{ex.gamification.niveaux.length}
            </span>
          ) : null}
        </div>
      </div>
      <span className="pst-ex__count">
        {ex.times_performed > 0
          ? t("smartPerformedTimes", { count: ex.times_performed })
          : t("smartNotPerformedYet")}
      </span>
    </div>
  );
}

function CollapsibleSection({ title, hint, defaultOpen = false, children, className = "" }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`pst-card pst-collapse ${open ? "is-open" : ""} ${className}`.trim()}>
      <button
        type="button"
        className="pst-collapse__head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>
          <strong className="pst-h pst-collapse__title">{title}</strong>
          {hint && !open ? <em className="pst-collapse__hint">{hint}</em> : null}
        </span>
        <span className="pst-collapse__chevron" aria-hidden>
          {open ? "▾" : "▸"}
        </span>
      </button>
      {open ? <div className="pst-collapse__body">{children}</div> : null}
    </div>
  );
}

function techProfileEntries(technicalProfile, lang) {
  const byCb = technicalProfile?.by_cb || {};
  return Object.entries(byCb)
    .filter(([, entry]) => {
      if (!entry || !String(entry.label || "").trim()) return false;
      if (entry.source === "leisure_lite" && entry.label === "leisure_baseline") return false;
      return true;
    })
    .sort(([a], [b]) => (PROFILING_CB_ORDER[a] || 99) - (PROFILING_CB_ORDER[b] || 99))
    .map(([cbKey, entry]) => ({
      cbKey,
      blockLabel: cbLabel(cbKey, lang),
      answer: String(entry.label || "").trim(),
      levelScore: entry.level_score,
      questionSnippet: entry.question_snippet,
      isDefault: entry.is_default === true || entry.source === "classement_default",
    }));
}

function TechnicalProfileSection({
  technicalProfile,
  defaultTechnicalProfile,
  profilingProgress,
  isCompetencyblocDone,
  lang,
  t,
  canEdit = false,
  onAskPingflo,
}) {
  const personalEntries = useMemo(
    () =>
      techProfileEntries(technicalProfile, lang).filter((row) => !row.isDefault),
    [technicalProfile, lang]
  );
  const defaultEntries = useMemo(
    () => techProfileEntries(defaultTechnicalProfile, lang),
    [defaultTechnicalProfile, lang]
  );
  const usingDefault = personalEntries.length === 0 && defaultEntries.length > 0;
  const entries = usingDefault ? defaultEntries : personalEntries;

  const progress = profilingProgress || {};
  const complete = Boolean(isCompetencyblocDone || progress.complete);
  const started =
    personalEntries.length > 0 || Number(progress.answered) > 0;
  if (!entries.length && !complete && !canEdit) return null;

  const defaultClassement =
    defaultTechnicalProfile?.classement ||
    defaultTechnicalProfile?.band_classement_range ||
    null;

  return (
    <CollapsibleSection
      title={t("smartTechnicalProfile")}
      hint={
        usingDefault
          ? t("smartTechnicalProfileDefaultHint", {
              classement: defaultClassement || t("smartTechnicalProfileDefaultLevel"),
            })
          : t("smartTechnicalProfileHint")
      }
      defaultOpen={false}
    >
      <p className="pst-muted">
        {usingDefault
          ? t("smartTechnicalProfileDefaultHint", {
              classement: defaultClassement || t("smartTechnicalProfileDefaultLevel"),
            })
          : t("smartTechnicalProfileHint")}
      </p>
      {usingDefault ? (
        <p className="pst-tech-progress">
          <span className="pst-chip pst-chip--sec">{t("smartTechnicalProfileDefaultBadge")}</span>
          {defaultClassement
            ? ` ${t("smartTechnicalProfileDefaultFor", { classement: defaultClassement })}`
            : null}
        </p>
      ) : progress.total > 0 ? (
        <p className="pst-tech-progress">
          {complete
            ? t("smartTechnicalProfilingComplete")
            : t("smartTechnicalProfilingProgress", {
                answered: progress.answered || personalEntries.length,
                total: progress.total,
              })}
        </p>
      ) : null}
      {entries.length === 0 ? (
        <p className="pst-empty-copy">{t("smartTechnicalProfilingEmpty")}</p>
      ) : (
        <ul className="pst-tech-list">
          {entries.map((row) => (
            <li key={row.cbKey} className="pst-tech-item">
              <div className="pst-tech-item__head">
                <span className="pst-tech-item__block">{row.blockLabel}</span>
                {row.levelScore != null ? (
                  <span className="pst-chip pst-chip--sec">
                    {t("playingLevel")} {row.levelScore}/4
                  </span>
                ) : null}
              </div>
              <p className="pst-tech-item__answer">{row.answer}</p>
              {row.questionSnippet ? <p className="pst-tech-item__question">{row.questionSnippet}</p> : null}
            </li>
          ))}
        </ul>
      )}
      <p className="pst-muted">
        <Link to="/tactical">{t("schemaLibraryTitle", "Tactical patterns")}</Link>
      </p>
      {canEdit ? (
        <>
          {usingDefault ? (
            <p className="pst-muted">{t("smartTechnicalProfilePersonalizeHint")}</p>
          ) : complete ? (
            <p className="pst-muted">{t("smartTechnicalProfileUpdateHint")}</p>
          ) : null}
          <button
            type="button"
            className="pst-sq-cta"
            onClick={() => onAskPingflo?.(complete ? "update" : "start")}
          >
            {complete
              ? t("smartTechnicalProfileUpdate")
              : usingDefault
                ? t("smartTechnicalProfilePersonalize")
                : started
                  ? t("smartTechnicalProfileContinue")
                  : t("smartTechnicalProfileStart")}
          </button>
        </>
      ) : null}
    </CollapsibleSection>
  );
}

function SpecificPanel({ data, trail, quests, coachA, userId, t, lang, openBlock, setOpenBlock, canEdit = false }) {
  const seasonMonths = data?.seasonHorizon?.remainingMonths ?? 4;
  const unclassifiedLabel = t("smartUnclassified");
  const purposeId = Number(trail?.slots?.S?.purposeId);
  const isLeisure = purposeId === 2;
  const isHealth = purposeId === 1;
  const isCompetition = purposeId === 3;
  const primary = quests.filter((q) => q.goal.is_primary);
  const secondary = quests.filter((q) => !q.goal.is_primary);
  const seasonQuest = data?.seasonQuest || data?.pathMeta?.seasonQuest || null;

  return (
    <>
      <SeasonBanner
        seasonHorizon={data.seasonHorizon}
        seasonQuest={seasonQuest}
        trail={trail}
        t={t}
      />

      <div className="pst-quest-board" data-tour-id="profile-smart-training">
        <h3 className="pst-h">{isLeisure ? t("smartLeisureQuestBoard") : isHealth ? t("healthSmartTabTitle") : t("smartTrainingObjectives")}</h3>
        <p className="pst-muted">
          {t("smartTrainingObjectivesHint", {
            defaultValue: "Exercises linked to each training objective — not season results.",
          })}
        </p>
        {!isLeisure && !isHealth && data?.sprintProgress?.exercises?.length ? (
          <div className="pst-card pst-sprint-card">
            <SprintExerciseTable sprintProgress={data.sprintProgress} userId={userId} />
          </div>
        ) : null}
        {quests.length === 0 ? (
          <div className="pst-empty-copy">
            <p>{t("smartNoGoals")}</p>
            {isCompetition ? (
              <button
                type="button"
                className="pst-sq-cta"
                onClick={() =>
                  openDigitalTwin({
                    home_role: "player",
                    seedMessage: t("digipingTrainingAxesHomeSeed"),
                    seedVisible: false,
                    profilingIntro: "trainingAxes",
                  })
                }
              >
                {t("smartAskDigipingAxes")}
              </button>
            ) : null}
          </div>
        ) : null}
        {primary.map((q) => (
          <GoalQuestCard
            key={`p-${q.goal.attribute_id || q.goal.label}`}
            quest={q}
            primary
            coachA={coachA}
            userId={userId}
            t={t}
            seasonMonths={seasonMonths}
            isLeisure={isLeisure}
          />
        ))}
        {secondary.length ? (
          <div className="pst-quest-grid">
            {secondary.map((q) => (
              <GoalQuestCard
                key={`s-${q.goal.attribute_id || q.goal.label}`}
                quest={q}
                primary={false}
                coachA={coachA}
                userId={userId}
                t={t}
                seasonMonths={seasonMonths}
                isLeisure={isLeisure}
              />
            ))}
          </div>
        ) : null}
      </div>

      <TechnicalProfileSection
        technicalProfile={data.technicalProfile || trail?.slots?.technicalProfile}
        defaultTechnicalProfile={data.defaultTechnicalProfile}
        profilingProgress={data.profilingProgress}
        isCompetencyblocDone={data.isCompetencyblocDone}
        lang={lang}
        t={t}
        canEdit={canEdit}
        onAskPingflo={(mode) =>
          openPlayerTechnicalProfiling({
            t,
            profile: {
              isCompetencyblocDone: data.isCompetencyblocDone,
              profilingProgress: data.profilingProgress,
              technicalProfile: data.technicalProfile || trail?.slots?.technicalProfile,
            },
            mode,
          })
        }
      />

      <CollapsibleSection
        title={t("smartPersonalCatalogue")}
        hint={t("smartCatalogueHint")}
        defaultOpen={false}
      >
        <p className="pst-muted">{t("smartCatalogueHint")}</p>
        {(data.catalogue?.byCompetencyBlock || []).length === 0 ? (
          <p className="pst-empty-copy">{t("smartCatalogueEmpty")}</p>
        ) : (
          (data.catalogue.byCompetencyBlock || []).map((block) => {
            const id = block.cb_id;
            const open = openBlock === id;
            const blockIcon = competencyBlockIconSrc(block.name || block);
            const axisRoles = blockAxisRoles(block);
            return (
              <div key={id} className="pst-block">
                <button
                  type="button"
                  className={`pst-block__head ${open ? "is-open" : ""}`}
                  onClick={() => setOpenBlock(open ? null : id)}
                  aria-expanded={open}
                >
                  <span>
                    {blockIcon ? (
                      <span className="pst-block__mark">
                        <ExerciseTypeMark src={blockIcon} />
                      </span>
                    ) : null}
                    {block.cb_id === "unclassified" ? unclassifiedLabel : block.name || unclassifiedLabel}
                    {block.goal_label ? (
                      <em className="pst-axis"> · {block.goal_label}</em>
                    ) : block.theme_axis && block.cb_id !== "unclassified" ? (
                      <em className="pst-axis"> · {block.theme_axis}</em>
                    ) : null}
                  </span>
                  <span className="pst-block__aside">
                    {axisRoles.map((role) => (
                      <span
                        key={role}
                        className={`pst-chip ${role === "primary" ? "pst-chip--pri" : "pst-chip--sec"}`}
                      >
                        {role === "primary" ? t("smartAxisPrimary") : t("smartAxisSecondary")}
                      </span>
                    ))}
                    <span className="pst-block__count">{block.exercises?.length || 0}</span>
                  </span>
                </button>
                {open ? (
                  <div className="pst-block__body">
                    {(block.exercises || []).map((ex) => (
                      <ExerciseRow key={ex.ex_id || ex.name} ex={ex} t={t} />
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </CollapsibleSection>
    </>
  );
}

function personalizedExerciseIds(data) {
  const ids = new Set();
  const add = (v) => {
    const s = v != null ? String(v).trim() : "";
    if (s) ids.add(s);
  };
  add(data?.personalizedExercise?.exerciseId || data?.personalizedExercise?.ex_id);
  for (const ge of data?.goalExercises || []) {
    add(ge.exerciseId || ge.ex_id);
  }
  for (const ex of data?.sprintProgress?.exercises || []) {
    add(ex.exerciseId || ex.ex_id);
  }
  return ids;
}

function autoevalTendency(autoevaluations = [], personalizedIds = null) {
  const rows = (autoevaluations || [])
    .filter((ae) => {
      if (!personalizedIds || personalizedIds.size === 0) return true;
      const id = ae.ex_id != null ? String(ae.ex_id) : "";
      return id && personalizedIds.has(id);
    })
    .slice()
    .reverse(); // oldest → newest for tendency
  const points = rows
    .map((ae) => {
      const tech = Number(ae.exercise_perf);
      return Number.isFinite(tech) ? tech : null;
    })
    .filter((n) => n != null);
  if (points.length < 2) {
    return { points, delta: null, latest: points[points.length - 1] ?? null, rows: rows.slice().reverse() };
  }
  const first = points[0];
  const last = points[points.length - 1];
  return {
    points,
    delta: Math.round((last - first) * 10) / 10,
    latest: last,
    rows: rows.slice().reverse(),
  };
}

function presenceRatio(present, expected) {
  const p = Number(present);
  const e = Number(expected);
  if (!Number.isFinite(p) || !Number.isFinite(e) || e <= 0) return null;
  return Math.max(0, Math.min(100, Math.round((p / e) * 100)));
}

function MeasurablePanel({
  data,
  trail,
  t,
  lang,
  canNotify = false,
  onNotify,
  notifying = false,
}) {
  const slotsM = trail?.slots?.M || {};
  const spw =
    parseFloat(slotsM.weeklyTraining?.sessionsPerWeek) ||
    Number(data?.dualHorizon?.half_season?.presence?.sessions_per_week) ||
    Number(data?.playerPath?.season_presence?.sessions_per_week) ||
    null;
  const cycleWeeks = Number(data?.dualHorizon?.sprint?.weeks || trail?.slots?.T?.cycleWeeks) || 3;
  const cycleP = data?.dualHorizon?.sprint?.presence || data?.cyclePresence || null;
  const seasonP = data?.dualHorizon?.half_season?.presence || data?.playerPath?.season_presence || null;

  // Always pair "attended" with "total planned" for that horizon (not "due by now").
  const cycleAttended = cycleP?.present_sessions ?? cycleP?.present ?? null;
  const cyclePlanned =
    cycleP?.expected_sessions ??
    (spw != null ? Math.round(Number(spw) * cycleWeeks) : null);
  const cycleDueNow = cycleP?.expected_to_date ?? null;

  const seasonAttended = seasonP?.present_sessions ?? seasonP?.present ?? null;
  const seasonPlanned = seasonP?.expected_sessions ?? null;
  const seasonDueNow = seasonP?.expected_to_date ?? null;

  const personalizedIds = personalizedExerciseIds(data);
  const tendency = autoevalTendency(data?.autoevaluations || [], personalizedIds);
  const lastSessionAe = tendency.rows[0] || null;
  const trendLabel =
    tendency.delta == null
      ? t("smartAeTrendFlat", { defaultValue: "Not enough auto-evaluations yet" })
      : tendency.delta > 0.2
        ? t("smartAeTrendUp", { defaultValue: "Improving" })
        : tendency.delta < -0.2
          ? t("smartAeTrendDown", { defaultValue: "Needs attention" })
          : t("smartAeTrendStable", { defaultValue: "Stable" });

  const formatAttendedPlanned = (attended, planned) => {
    if (attended != null && planned != null) return `${attended}/${planned}`;
    if (planned != null) return `0/${planned}`;
    if (attended != null) return String(attended);
    return "—";
  };

  return (
    <div className="pst-m">
      <FlttRankingSlip
        ranking={data?.officialRanking}
        history={data?.rankingHistory}
        t={t}
        showNotify={canNotify}
        onNotify={onNotify}
        notifying={notifying}
        showHistory
      />
      <div className="pst-meter-grid">
        <article className="pst-meter">
          <span className="pst-meter__kicker">{t("smartWeeklyVolume")}</span>
          <strong className="pst-meter__value">
            {spw != null ? `${spw}×` : "—"}
          </strong>
          <span className="pst-meter__unit">
            {t("smartWeeklyVolumeTarget", {
              defaultValue: "Your weekly club commitment",
            })}
          </span>
        </article>

        <article className="pst-meter">
          <span className="pst-meter__kicker">
            {t("smartCycleSessions", { defaultValue: "This training cycle" })}
          </span>
          <strong className="pst-meter__value">
            {formatAttendedPlanned(cycleAttended, cyclePlanned)}
          </strong>
          <span className="pst-meter__unit">
            {t("smartCycleSessionsExplain", {
              weeks: cycleWeeks,
              defaultValue:
                "Club sessions attended / planned for this {{weeks}}-week objectives cycle",
            })}
          </span>
          {cycleDueNow != null ? (
            <span className="pst-meter__pace">
              {t("smartPresenceDueSoFar", {
                count: cycleDueNow,
                defaultValue: "{{count}} expected by now",
              })}
            </span>
          ) : null}
          {presenceRatio(cycleAttended, cyclePlanned) != null ? (
            <ProgressBar
              value={presenceRatio(cycleAttended, cyclePlanned)}
              accent="linear-gradient(90deg, #34d399, #059669)"
            />
          ) : null}
        </article>

        <article className="pst-meter">
          <span className="pst-meter__kicker">
            {t("smartMidSeasonSessions", { defaultValue: "To mid-season" })}
          </span>
          <strong className="pst-meter__value">
            {formatAttendedPlanned(seasonAttended, seasonPlanned)}
          </strong>
          <span className="pst-meter__unit">
            {t("smartMidSeasonSessionsExplain", {
              defaultValue:
                "Club sessions attended / planned from season start to mid-season",
            })}
          </span>
          {seasonDueNow != null ? (
            <span className="pst-meter__pace">
              {t("smartPresenceDueSoFar", {
                count: seasonDueNow,
                defaultValue: "{{count}} expected by now",
              })}
            </span>
          ) : null}
          {presenceRatio(seasonAttended, seasonPlanned) != null ? (
            <ProgressBar
              value={presenceRatio(seasonAttended, seasonPlanned)}
              accent="linear-gradient(90deg, #a78bfa, #7c3aed)"
            />
          ) : null}
        </article>
      </div>

      <div className="pst-card pst-ae-card">
        <div className="pst-ae-card__head">
          <h3 className="pst-h">{t("smartAeTendency", { defaultValue: "Auto-evaluation tendency" })}</h3>
          <span
            className={`pst-chip ${
              tendency.delta > 0.2
                ? "pst-chip--ok"
                : tendency.delta < -0.2
                  ? "pst-chip--warn"
                  : "pst-chip--sec"
            }`}
          >
            {trendLabel}
            {tendency.delta != null ? ` · ${tendency.delta > 0 ? "+" : ""}${tendency.delta}` : ""}
          </span>
        </div>
        <p className="pst-muted">
          {t("smartAeTendencyHint", {
            defaultValue: "Personalized exercises — technical score over recent sessions",
          })}
        </p>
        {tendency.points.length ? (
          <div className="pst-ae-spark" aria-hidden="true">
            {tendency.points.slice(-8).map((p, i) => (
              <span
                key={`${p}-${i}`}
                className="pst-ae-spark__bar"
                style={{ height: `${Math.max(12, (p / 5) * 100)}%` }}
                title={`${p}/5`}
              />
            ))}
          </div>
        ) : (
          <p className="pst-empty-copy">{t("smartNoAutoevals")}</p>
        )}

        <div className="pst-ae-last">
          <span className="pst-meter__kicker">
            {t("smartAeLastSession", { defaultValue: "Last session · personalized exercise" })}
          </span>
          {lastSessionAe ? (
            <div className="pst-ae-last__row">
              <strong>{lastSessionAe.exercise_name || t("exercise")}</strong>
              <span>
                {t("smartTechShort")} {lastSessionAe.exercise_perf ?? "—"} · {t("smartRpeShort")}{" "}
                {lastSessionAe.perceived_exertion ?? "—"}
              </span>
              <em>{formatDate(lastSessionAe.at, lang)}</em>
            </div>
          ) : (
            <p className="pst-empty-copy">{t("smartNoAutoevals")}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function presenceMilestoneForPurpose(purposeId, t, sessionsPerWeek = null) {
  if (purposeId === 1) {
    const n = sessionsPerWeek || 3;
    return t("smartPresenceMilestoneHealth", {
      count: n,
      defaultValue: `Keep ${n} sessions / week for health & fitness`,
    });
  }
  if (purposeId === 2) {
    return t("smartPresenceMilestoneLeisure", {
      defaultValue: "Come to the club most weeks (at least one session)",
    });
  }
  return t("smartDefaultPresenceMilestone", {
    defaultValue: "Train most weeks at least two sessions per week",
  });
}

function isCycleMilestone(m, seasonResultLabel) {
  const label = String(m?.label || "").trim();
  if (!label) return false;
  if (isSignupSeasonResultNoise(label)) return false;
  if (isKnownSignupSeasonResultLabel(label)) return false;
  if (seasonResultLabel && label === seasonResultLabel) return false;
  if (/^Progress on /i.test(label)) return false;
  if (/train most weeks at least two sessions/i.test(label)) return false;
  if (/au moins deux séances/i.test(label)) return false;
  if (/come to the club most weeks/i.test(label)) return false;
  if (/Keep \d+ sessions \/ week for health/i.test(label)) return false;
  if (m?.source === "default_presence") return false;
  // Cycle milestones: coach/custom, leisure challenge, end-of-cycle review
  return true;
}

function AchievablePanel({ data, trail, coachA, t, lang }) {
  const slotsA = trail?.slots?.A || {};
  const purposeId = Number(trail?.slots?.S?.purposeId);
  const spw = parseFloat(trail?.slots?.M?.weeklyTraining?.sessionsPerWeek) || null;
  const seasonQuest = data?.seasonQuest || data?.pathMeta?.seasonQuest || null;
  const seasonResultLabel = resolveSignupSeasonResult(
    seasonQuest,
    data?.seasonHorizon,
    slotsA,
    t
  );
  const presenceLabel = presenceMilestoneForPurpose(purposeId, t, spw);
  const cycleMilestones = (data.milestones || []).filter((m) =>
    isCycleMilestone(m, seasonResultLabel)
  );
  const coachKey =
    coachA === "approved"
      ? "smartTargetApproved"
      : coachA === "rejected"
        ? "smartTargetRejected"
        : coachA === "pending"
          ? "smartTargetPendingCoach"
          : "smartCoachPactNone";
  return (
    <>
      <div className="pst-card pst-pact">
        <h3 className="pst-h">A · {t("smartMilestones")}</h3>
        <p className="pst-muted">
          {t("smartMilestonesHint", {
            defaultValue:
              "Presence first. Your coach can add technical cycle milestones later.",
          })}
        </p>
        <span className={`pst-chip ${coachA === "approved" ? "pst-chip--ok" : coachA === "rejected" ? "pst-chip--warn" : "pst-chip--now"}`}>
          {t(coachKey)}
        </span>
        {slotsA.grip || slotsA.hand ? (
          <p className="pst-muted">
            {[slotsA.grip && `${t("smartGrip")}: ${slotsA.grip}`, slotsA.hand && `${t("smartHand")}: ${slotsA.hand}`]
              .filter(Boolean)
              .join(" · ")}
          </p>
        ) : null}
      </div>
      <div className="pst-card">
        <ol className="pst-miles">
          <li className="is-upcoming pst-miles__presence">
            <div className="pst-miles__top">
              <span className="pst-miles__label">{presenceLabel}</span>
              <span className="pst-chip pst-chip--ok">
                {t("smartPresenceMilestoneBadge", { defaultValue: "Presence" })}
              </span>
            </div>
            <span className="pst-miles__when">
              {t("smartDefaultPresenceMilestoneHint", {
                defaultValue: "Default presence target · coach may add more",
              })}
            </span>
          </li>
          {cycleMilestones.map((m, i) => (
            <li key={`${m.label}-${i}`} className={`is-${m.status || "upcoming"}`}>
              <div className="pst-miles__top">
                <span className="pst-miles__label">{m.label}</span>
                <span className={`pst-chip ${m.realistic !== false ? "pst-chip--ok" : "pst-chip--warn"}`}>
                  {m.horizon === "mid" ? t("smartHorizonMid") : t("smartHorizonLong")}
                </span>
              </div>
              <span className="pst-miles__when">
                {m.due ? formatDate(m.due, lang) : t("smartCycle", { defaultValue: "Cycle" })}
              </span>
            </li>
          ))}
        </ol>
      </div>
    </>
  );
}

function RelevantPanel({ trail, data, coachR, t }) {
  const why = trail?.slots?.R?.whyItMatters;
  const note = data?.coachAgreement?.note;
  const coachKey =
    coachR === "approved"
      ? "smartCoachPactApproved"
      : coachR === "rejected"
        ? "smartCoachPactRejected"
        : coachR === "pending"
          ? "smartCoachPactPending"
          : "smartCoachPactNone";
  return (
    <div className="pst-card pst-pact">
      <h3 className="pst-h">{t("smartWhyItMatters")}</h3>
      {why ? <p className="pst-lead">{why}</p> : <p className="pst-empty-copy">{t("smartDimensionEmpty")}</p>}
      <div className="pst-pact__seal">
        <span className="pst-quest__kicker">{t("smartCoachPact")}</span>
        <span className={`pst-chip ${coachR === "approved" ? "pst-chip--ok" : coachR === "rejected" ? "pst-chip--warn" : "pst-chip--now"}`}>
          {t(coachKey)}
        </span>
        {note ? <p className="pst-muted">{note}</p> : null}
      </div>
    </div>
  );
}

function TimeboundPanel({ data, trail, t, lang }) {
  const slotsT = trail?.slots?.T || {};
  const cycleWeeks = Number(data?.dualHorizon?.sprint?.weeks || slotsT.cycleWeeks) || 3;
  const start = slotsT.cycleStart;
  const sprintP = data?.dualHorizon?.sprint?.presence || data?.cyclePresence;
  const seasonP = data?.dualHorizon?.half_season?.presence || data?.playerPath?.season_presence;
  const seasonHorizon = data?.seasonHorizon || data?.dualHorizon?.half_season || {};
  const seasonWeeksLeft = seasonHorizon?.remainingWeeks ?? null;
  const seasonMonthsLeft = seasonHorizon?.remainingMonths ?? null;
  const seasonTarget = seasonHorizon?.targetDate || null;
  const seasonResultLabel = resolveSignupSeasonResult(
    data?.seasonQuest || data?.pathMeta?.seasonQuest || null,
    seasonHorizon,
    trail?.slots?.A || null,
    t
  );

  const cyclePresent = sprintP?.present_sessions ?? sprintP?.present ?? 0;
  const cycleExpected =
    sprintP?.expected_to_date ??
    sprintP?.expected ??
    sprintP?.expected_sessions ??
    null;
  const spw = Number(seasonP?.sessions_per_week) || Number(trail?.slots?.M?.weeklyTraining?.sessionsPerWeek) || 2;
  const cycleTargetSessions = Math.round(spw * cycleWeeks);
  const cycleRemaining =
    cycleExpected != null
      ? Math.max(0, Number(cycleExpected) - Number(cyclePresent || 0))
      : Math.max(0, cycleTargetSessions - Number(cyclePresent || 0));

  const seasonPresent = seasonP?.present_sessions ?? seasonP?.present ?? 0;
  const seasonExpected =
    seasonP?.expected_to_date ?? seasonP?.expected ?? seasonP?.expected_sessions ?? null;
  const seasonPlanned = seasonP?.expected_sessions ?? seasonExpected;
  const seasonRemaining =
    seasonPlanned != null ? Math.max(0, Number(seasonPlanned) - Number(seasonPresent || 0)) : null;

  let cycleElapsedPct = 35;
  if (start) {
    const startMs = new Date(start).getTime();
    const endMs = startMs + cycleWeeks * 7 * 24 * 60 * 60 * 1000;
    if (Number.isFinite(startMs) && endMs > startMs) {
      cycleElapsedPct = Math.max(
        0,
        Math.min(100, Math.round(((Date.now() - startMs) / (endMs - startMs)) * 100))
      );
    }
  } else if (cycleExpected) {
    cycleElapsedPct = presenceRatio(cyclePresent, cycleExpected) ?? 35;
  }

  const seasonElapsedPct =
    seasonWeeksLeft != null
      ? Math.max(0, Math.min(100, 100 - Math.round((seasonWeeksLeft / Math.max(seasonWeeksLeft + 4, 16)) * 100)))
      : presenceRatio(seasonPresent, seasonExpected) ?? 40;

  return (
    <div className="pst-t">
      <article className="pst-card pst-track pst-track--season">
        <header className="pst-track__head">
          <span className="pst-meter__kicker">{t("playerPathSeasonClock")}</span>
          <h3 className="pst-h">{t("smartMidSeason")}</h3>
        </header>
        {seasonResultLabel ? (
          <p className="pst-track__goal">{seasonResultLabel}</p>
        ) : null}
        <div className="pst-track__stats">
          <div>
            <strong>{seasonWeeksLeft != null ? seasonWeeksLeft : "—"}</strong>
            <span>{t("smartWeeksRemaining")}</span>
          </div>
          <div>
            <strong>
              {seasonPresent}
              {seasonPlanned != null ? `/${seasonPlanned}` : ""}
            </strong>
            <span>{t("smartMidSeasonSessions", { defaultValue: "Sessions to mid-season" })}</span>
          </div>
          <div>
            <strong>{seasonRemaining != null ? seasonRemaining : "—"}</strong>
            <span>{t("smartSessionsRemaining", { defaultValue: "sessions left" })}</span>
          </div>
        </div>
        <div className="pst-track__bar" aria-hidden="true">
          <span className="pst-track__fill" style={{ width: `${seasonElapsedPct}%` }} />
          <span className="pst-track__now" style={{ left: `${seasonElapsedPct}%` }} />
        </div>
        <p className="pst-muted">
          {seasonTarget ? formatDate(seasonTarget, lang) : t("smartSeasonHorizon")}
          {seasonMonthsLeft != null ? ` · ${t("smartSeasonMonthsLeft", { count: seasonMonthsLeft })}` : ""}
        </p>
      </article>

      <article className="pst-card pst-track pst-track--cycle">
        <header className="pst-track__head">
          <span className="pst-meter__kicker">{t("playerPathSprintClock")}</span>
          <h3 className="pst-h">
            {t("smartCycleLength", { weeks: cycleWeeks, defaultValue: `${cycleWeeks} week cycle` })}
          </h3>
        </header>
        <p className="pst-track__goal">
          {t("smartCycleObjectivesHint", {
            defaultValue: "Training objectives cycle",
          })}
        </p>
        <div className="pst-track__stats">
          <div>
            <strong>{cycleWeeks}</strong>
            <span>{t("smartCycleWeeks", { defaultValue: "weeks" })}</span>
          </div>
          <div>
            <strong>
              {cyclePresent}
              {cycleExpected != null || cycleTargetSessions
                ? `/${cycleExpected ?? cycleTargetSessions}`
                : ""}
            </strong>
            <span>{t("smartCycleSessions", { defaultValue: "Cycle sessions" })}</span>
          </div>
          <div>
            <strong>{cycleRemaining}</strong>
            <span>{t("smartSessionsRemaining", { defaultValue: "sessions left" })}</span>
          </div>
        </div>
        <div className="pst-track__bar" aria-hidden="true">
          <span className="pst-track__fill pst-track__fill--cycle" style={{ width: `${cycleElapsedPct}%` }} />
          <span className="pst-track__now" style={{ left: `${cycleElapsedPct}%` }} />
        </div>
        <ol className="pst-track__ticks">
          <li className={cycleElapsedPct < 15 ? "is-current" : ""}>
            <strong>{t("smartNow")}</strong>
            <span>{start ? formatDate(start, lang) : t("smartCycleStart")}</span>
          </li>
          <li className={cycleElapsedPct >= 15 && cycleElapsedPct < 85 ? "is-current" : ""}>
            <strong>{t("smartHorizonMid")}</strong>
            <span>{t("smartCycle", { defaultValue: "Cycle" })}</span>
          </li>
          <li className={cycleElapsedPct >= 85 ? "is-current" : ""}>
            <strong>{t("smartHorizonLong")}</strong>
            <span>{t("smartCycleLength", { weeks: cycleWeeks })}</span>
          </li>
        </ol>
      </article>
    </div>
  );
}

export function ProfileSmartTab({ userId = null }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];
  const tourDemo = useTourDemoActive();
  const [data, setData] = useState(() => (tourDemo ? demoSmartTrail() : null));
  const [loading, setLoading] = useState(!tourDemo);
  const [error, setError] = useState(null);
  const [activeDim, setActiveDim] = useState("S");
  const [openBlock, setOpenBlock] = useState(null);
  const [notifyingRank, setNotifyingRank] = useState(false);

  const load = useCallback(async () => {
    if (tourDemo) {
      setData((prev) => prev || demoSmartTrail());
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      let trail = await fetchSmartTrail(userId, lang);
      if (
        trail &&
        !canShowFlttRankingChart(trail.rankingHistory)
      ) {
        try {
          const statsRes = await getPlayerProfileStats(
            userId ? { view_user_id: userId } : undefined
          );
          const stats = statsRes?.data?.data;
          if (stats?.rankingHistory?.length || stats?.officialRanking) {
            trail = {
              ...trail,
              officialRanking:
                trail.officialRanking || stats.officialRanking || null,
              rankingHistory: stats.rankingHistory?.length
                ? stats.rankingHistory
                : trail.rankingHistory || [],
              rankingNoticePending:
                trail.rankingNoticePending ??
                Boolean(stats.officialRanking?.rankingNoticePending),
            };
          }
        } catch (statsErr) {
          console.warn(
            "ProfileSmartTab ranking stats:",
            statsErr?.message || statsErr
          );
        }
      }
      if (trail) {
        setData(trail);
        if (trail?.trail?.activeDimension) setActiveDim(trail.trail.activeDimension);
        const firstCb = trail?.catalogue?.byCompetencyBlock?.[0]?.cb_id;
        if (firstCb) setOpenBlock(firstCb);
      } else if (tourDemo) {
        setData(demoSmartTrail());
      } else {
        setData(null);
      }
    } catch (err) {
      if (tourDemo) {
        setData(demoSmartTrail());
        setError(null);
      } else {
        setError(err?.response?.data?.message || err?.message || t("smartTrailLoadError"));
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  }, [userId, lang, t, tourDemo]);

  useEffect(() => {
    load();
  }, [load]);

  const trail = data?.trail;
  const isLeisure = Number(trail?.slots?.S?.purposeId) === 2;
  const isHealth = Number(trail?.slots?.S?.purposeId) === 1;
  const quests = useMemo(() => buildQuests(data), [data]);
  const coachA = coachDimStatus(data?.coachAgreement, trail?.coachPendingDims, "A");
  const coachR = coachDimStatus(data?.coachAgreement, trail?.coachPendingDims, "R");
  const playerUserId = userId || data?.user_id || trail?.user_id || null;
  const storedUser = readStoredUser();
  const isSelf =
    !tourDemo &&
    storedUser?.user_id &&
    (!playerUserId || String(playerUserId) === String(storedUser.user_id));
  const rankingNoticePending = Boolean(data?.rankingNoticePending);
  const coachPendingDims = useMemo(() => {
    const dims = [...(trail?.coachPendingDims || [])];
    if (rankingNoticePending && !dims.includes("M")) dims.push("M");
    return dims;
  }, [trail?.coachPendingDims, rankingNoticePending]);
  const canNotifyRank = Boolean(userId) && !isSelf && rankingNoticePending;
  const notifyRanking = useCallback(async () => {
    if (!playerUserId || notifyingRank) return;
    setNotifyingRank(true);
    try {
      await acknowledgeFlttRankingNotice(playerUserId);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t("smartTrailLoadError"));
    } finally {
      setNotifyingRank(false);
    }
  }, [playerUserId, notifyingRank, load, t]);

  if (loading && !data) {
    return (
      <div className="pst" data-tour-id="profile-smart-tab">
        <span className="ios-activity-sub">{t("loading")}</span>
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="pst" data-tour-id="profile-smart-tab">
        <div className="pst-card pst-empty">{error}</div>
      </div>
    );
  }
  if (!data) return null;

  if (isLeisure) {
    return (
      <div className="pst pst--leisure-challenges" data-tour-id="profile-smart-tab">
        <span className="ios-stats-title">{t("challengesTab")}</span>
        <div className="pst-card pst-rail-card pst-board pst-board--s" data-tour-id="profile-smart-objectives">
          <DigipingSmartRail
            status={trail?.status || {}}
            activeDimension={activeDim}
            labels={trail?.labels || {}}
            coachPendingDims={coachPendingDims}
            coachCaption={t("smartCoachShort")}
            onSelectDimension={setActiveDim}
          />
        </div>
        <div className="pst-card">
          <h3 className="pst-h">{t("smartLeisureQuestBoard")}</h3>
          <p className="pst-lead">{t("smartLeisureChallengesLead")}</p>
        </div>
        <SpecificPanel
          data={data}
          trail={trail}
          quests={quests}
          coachA={coachA}
          userId={playerUserId}
          t={t}
          lang={lang}
          openBlock={openBlock}
          setOpenBlock={setOpenBlock}
          canEdit={Boolean(isSelf)}
        />
      </div>
    );
  }

  return (
    <div className={`pst ${isHealth ? "pst--health" : ""}`} data-tour-id="profile-smart-tab">
      <span className="ios-stats-title">{isHealth ? t("healthSmartTabTitle") : t("smartTrailTitle")}</span>
      {isHealth ? (
        <div className="pst-card pst-health-banner">
          <h3 className="pst-h">{t("healthSmartBannerTitle")}</h3>
          <p className="pst-lead">{t("healthSmartBannerLead")}</p>
          {data?.pathMeta?.healthQuest?.questTitle || trail?.slots?.health?.questTitle ? (
            <p className="pst-muted">{data?.pathMeta?.healthQuest?.questTitle || trail?.slots?.health?.questTitle}</p>
          ) : null}
        </div>
      ) : null}

      <div
        className={`pst-card pst-rail-card pst-board pst-board--${activeDim.toLowerCase()}`}
        data-tour-id="profile-smart-objectives"
      >
        <DigipingSmartRail
          status={trail?.status || {}}
          activeDimension={activeDim}
          labels={trail?.labels || {}}
          coachPendingDims={coachPendingDims}
          coachCaption={t("smartCoachShort")}
          onSelectDimension={setActiveDim}
        />
        <p className={`pst-dim-hint pst-dim-hint--${activeDim.toLowerCase()}`}>
          <strong className="pst-dim-hint__letter">{activeDim}</strong>
          {t(DIM_HINT_KEYS[activeDim] || "smartDimensionEmpty")}
        </p>
      </div>

      <div key={activeDim} className={`pst-dim-panel pst-dim-panel--${activeDim.toLowerCase()}`}>
        {activeDim === "S" ? (
          <SpecificPanel
            data={data}
            trail={trail}
            quests={quests}
            coachA={coachA}
            userId={playerUserId}
            t={t}
            lang={lang}
            openBlock={openBlock}
            setOpenBlock={setOpenBlock}
            canEdit={Boolean(isSelf)}
          />
        ) : null}
        {activeDim === "M" ? (
          <MeasurablePanel
            data={data}
            trail={trail}
            t={t}
            lang={lang}
            canNotify={canNotifyRank}
            onNotify={notifyRanking}
            notifying={notifyingRank}
          />
        ) : null}
        {activeDim === "A" ? <AchievablePanel data={data} trail={trail} coachA={coachA} t={t} lang={lang} /> : null}
        {activeDim === "R" ? <RelevantPanel trail={trail} data={data} coachR={coachR} t={t} /> : null}
        {activeDim === "T" ? <TimeboundPanel data={data} trail={trail} t={t} lang={lang} /> : null}
      </div>
    </div>
  );
}
